import React, { useCallback, useEffect, useState } from 'react';
import { Loader } from 'src/@vymo/ui/atoms';
import Button from 'src/@vymo/ui/atoms/button';
import { CardGrid } from 'src/@vymo/ui/blocks';
import Widget from 'src/@vymo/ui/molecules/widget';
import { PERFORMANCE_DASHBOARD } from 'src/modules/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { renderModule } from 'src/workspace/slice';
import { getFilterData, getPerformanceSnapshot } from '../selectors';
import { fetchPerformanceSnapshot } from '../thunk';
import PerformanceSnapshotCard from './PerformanceSnapshotCard';
import styles from './index.module.scss';

function PerformanceSnapshot() {
  const filterData = useAppSelector(getFilterData);
  const performanceSnapshotData = useAppSelector(getPerformanceSnapshot);
  const dispatch = useAppDispatch();
  const [, setNoDataCards] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (filterData?.filterData) {
      setLoading(true);
      const params = filterData.filterData;
      dispatch(fetchPerformanceSnapshot({ params })).finally(() => {
        setLoading(false);
      });
    }
  }, [dispatch, filterData]);

  const adjustNumberOfCards = useCallback((type: string) => {
    setNoDataCards((prevNoDataCards = []) => {
      const alreadyExists = prevNoDataCards.findIndex(
        (dataCardType) => dataCardType === type,
      );
      if (alreadyExists < 0) {
        return [...prevNoDataCards, type];
      }
      return prevNoDataCards;
    });
  }, []);

  const clickOnMoreDetails = useCallback(() => {
    dispatch(
      renderModule({
        id: PERFORMANCE_DASHBOARD,
        props: {
          tab: 'dashboard',
        },
      }),
    );
  }, [dispatch]);

  const handleErrorRetry = useCallback(() => {
    if (filterData?.filterData) {
      setLoading(true);
      dispatch(
        fetchPerformanceSnapshot({ params: filterData.filterData }),
      ).finally(() => {
        setLoading(false);
      });
    }
  }, [dispatch, filterData]);

  const handleErrorKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleErrorRetry();
      }
    },
    [handleErrorRetry],
  );

  const renderErrorView = () => {
    if (!performanceSnapshotData || performanceSnapshotData.length === 0) {
      return (
        <div
          className={styles.performanceSnapshot__error}
          onClick={handleErrorRetry}
          onKeyDown={handleErrorKeyDown}
          role="button"
          tabIndex={0}
        >
          <div className={styles.performanceSnapshot__errorMessage}>
            Failed to load performance snapshot
          </div>
        </div>
      );
    }
    return null;
  };

  const renderCards = () => {
    if (loading) {
      return (
        <Loader
          className={styles.performanceSnapshot__loader}
          visible
          size="small"
        />
      );
    }

    if (!performanceSnapshotData || performanceSnapshotData.length === 0) {
      return renderErrorView();
    }

    return (
      <CardGrid classNames={styles.performanceSnapshot__cards}>
        {performanceSnapshotData.map((item, index) => {
          const cardKey = item.code || `card-${index}`;
          return (
            <PerformanceSnapshotCard
              key={cardKey}
              snapshotCard={item}
              adjustNumberOfCards={adjustNumberOfCards}
              metricsData={performanceSnapshotData}
            />
          );
        })}
      </CardGrid>
    );
  };

  return (
    <Widget
      title="Performance Snapshot"
      content={renderCards()}
      actions={
        <Button type="outlined" onClick={clickOnMoreDetails}>
          Show more
        </Button>
      }
    />
  );
}

export default PerformanceSnapshot;
